import { DateTime } from 'luxon';

import {
  getFreeToSpendForDate,
  getIncomeAfterBillsAndSavings,
  getLeftToSpend,
  getPlannedSpendingItemsForDate,
  getSpendingPlanBillsForDisplayV2,
  getSpendingPlanGoalsForDisplayV2,
  getSpendingPlanIncomeForDisplayV2,
  isFreeToSpendFutureMonth,
} from 'app-components/flux/free-to-spend/freeToSpendSelectors';
import { getLogger } from 'app-components/utils/core';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { safeParse } from '../../aiChatUtils';
import { returnToolResponse } from '../llmToolsFramework';
import type { ToolMessage } from '../llmToolsTypes';

const log = getLogger('llmSpendingPlanTools');

export const spendingPlanTools = {
  get_spending_plan_data: {
    definition: {
      name: 'get_spending_plan_data',
      description: `Retrieve spending plan data for a given month to answer questions about expected income, bills, goals, planned spending items, and how much the user expects to have left to spend. Use this for forward-looking or current-month questions like "How much will I have left this month?", "What are my planned expenses?", "What bills do I have coming up?", or "How much am I saving towards goals?". Date defaults to the current month if not provided. NEVER show internal id or filterId fields to the user.`,
      parameters: {
        type: 'object' as const,
        properties: {
          date: {
            type: 'string' as const,
            description: 'The month to retrieve, in YYYY-MM format. Defaults to current month.',
          },
        },
        required: [],
      },
    },
    handler: (msg: ToolMessage) => {
      const args = safeParse(msg.arguments) || {};
      const state = store?.getState() as RootState;

      const isoDate = args.date
        ? DateTime.fromFormat(args.date as string, 'yyyy-MM')
            .startOf('month')
            .toISODate()
        : DateTime.local().startOf('month').toISODate();

      if (!isoDate) {
        return returnToolResponse(msg, { error: 'Invalid date format. Use YYYY-MM.' });
      }

      const freeToSpend = getFreeToSpendForDate(state, isoDate);
      const isFuture = isFreeToSpendFutureMonth(state, freeToSpend);

      const income = getSpendingPlanIncomeForDisplayV2(state, freeToSpend);
      const bills = getSpendingPlanBillsForDisplayV2(state, freeToSpend);
      const goals = getSpendingPlanGoalsForDisplayV2(state, freeToSpend);
      const totalToSpend = getIncomeAfterBillsAndSavings(state, freeToSpend);
      const leftToSpend = getLeftToSpend(state, freeToSpend);
      const plannedItems = getPlannedSpendingItemsForDate(state, isoDate);

      log.debug('llmTools/spendingPlan: get_spending_plan_data', { date: isoDate, isFuture });

      return returnToolResponse(msg, {
        date: isoDate,
        isFutureMonth: isFuture,
        income: {
          includedAmount: income.includedAmount ?? null,
          excludedAmount: income.excludedAmount ?? null,
          overwrittenAmount: income.overwrittenAmount ?? null,
        },
        bills: {
          includedAmount: bills.includedAmount ?? null,
          excludedAmount: bills.excludedAmount ?? null,
          overwrittenAmount: bills.overwrittenAmount ?? null,
        },
        goals: {
          includedAmount: goals.includedAmount ?? null,
          excludedAmount: goals.excludedAmount ?? null,
          overwrittenAmount: goals.overwrittenAmount ?? null,
        },
        plannedSpending: plannedItems.toArray().map((item) => ({
          name: item.name ?? null,
          targetAmount: item.targetAmount,
          spentAmount: item.calculatedSpentAmount ?? null,
          rolloverType: item.rolloverType,
        })),
        totalToSpend: totalToSpend ?? null,
        leftToSpend: leftToSpend ?? null,
      });
    },
  },
};
